// src/components/admin/AdminBlog.tsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import AdminPostForm from './AdminPostForm';
import { BlogPost } from '../../types/blog';
import api from "../../api/axiosInstance"; // Plný typ pre úpravu


const API_BASE_URL = process.env.NODE_ENV === 'development'
    ? 'http://localhost:8080/api/posts'
    : 'https://api.sonabelanska.sk/api/posts';


// Typ iba pre zobrazenie v zozname (ľahší, bez sections)
interface BlogPostListItem {
    id: number;
    title: string;
    excerpt: string;
    category: string;
    createdAt: string;
    readTime: string;
    featuredImage?: string;
}

const BlogContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    padding: 1rem;
`;

const BlogHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 1rem;
    margin-bottom: 2rem;
    flex-wrap: wrap;
`;

const TabButton = styled(motion.button)<{ active?: boolean }>`
    padding: 1rem 2rem;
    border: none;
    border-radius: 12px;
    background: ${props =>
            props.active
                    ? 'linear-gradient(135deg, #990147 0%, #cc3366 100%)'
                    : '#f1f5f9'};
    color: ${props => (props.active ? '#ffffff' : '#64748b')};
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 1rem;

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 6px 20px rgba(153, 1, 71, 0.25);
    }
`;

const PostsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
    gap: 2rem;
`;

const PostCard = styled(motion.div)`
    background: #ffffff;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    cursor: pointer;
    border: 1px solid #e2e8f0;

    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
    }
`;

const PostImage = styled.div<{ src?: string }>`
    height: 180px;
    background-image: url(${props => props.src || ''});
    background-size: cover;
    background-position: center;
    background-color: ${props => (props.src ? 'transparent' : '#f1f5f9')};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 3rem;
    color: #cbd5e1;
`;

const PostContent = styled.div`
    padding: 1.5rem;
`;

const PostTitle = styled.h3`
    font-size: 1.25rem;
    font-weight: 700;
    color: #000000;
    margin-bottom: 0.75rem;
    line-height: 1.4;
`;

const PostMeta = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

const CategoryBadge = styled.span`
  background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
  color: white;
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PostExcerpt = styled.p`
  color: #475569;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PostActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const ActionButton = styled(motion.button)<{ variant: 'edit' | 'delete' }>`
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;

  ${props =>
    props.variant === 'edit'
        ? `
    background: #dbeafe;
    color: #1e40af;
    &:hover { background: #bfdbfe; }
  `
        : `
    background: #fee2e2;
    color: #dc2626;
    &:hover { background: #fecaca; }
  `}
`;

const EmptyState = styled(motion.div)`
  text-align: center;
  padding: 4rem 2rem;
  background: #f8fafc;
  border-radius: 16px;
  border: 2px dashed #e2e8f0;
  color: #64748b;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #64748b;
  font-size: 1.1rem;
`;

const AdminBlog: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'create' | 'articles'>('articles');
    const [posts, setPosts] = useState<BlogPostListItem[]>([]);
    const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
    const [loading, setLoading] = useState(true);

    // Načítanie zoznamu článkov
    const fetchPosts = async () => {
        try {
            setLoading(true);
            const response = await fetch(API_BASE_URL);
            if (!response.ok) throw new Error('Nepodarilo sa načítať články');

            const data: BlogPost[] = await response.json();

            const listItems: BlogPostListItem[] = data.map(post => ({
                id: post.id,
                title: post.title,
                excerpt: post.excerpt || '',
                category: post.category.name,
                createdAt: post.createdAt,
                readTime: post.readTime || '5 min',
                featuredImage: post.featuredImage
            }));

            setPosts(listItems);
        } catch (error) {
            console.error('Chyba pri načítaní článkov:', error);
            alert('Nepodarilo sa načítať články');
        } finally {
            setLoading(false);
        }
    };

    // Načítanie plného článku na úpravu
    const handleEditPost = async (postId: number) => {
        try {
            const response = await fetch(`${API_BASE_URL}/${postId}`);
            if (!response.ok) throw new Error('Nepodarilo sa načítať článok');

            const fullPost: BlogPost = await response.json();
            setEditingPost(fullPost);
            setActiveTab('create');
        } catch (error) {
            alert('Nepodarilo sa načítať článok na úpravu');
        }
    };

    const handleDeletePost = async (postId: number) => {
        if (!window.confirm('Naozaj chcete odstrániť tento článok?')) return;

        try {
            const response = await api(`${API_BASE_URL}/${postId}`, {
                method: 'DELETE'
            });

            if (response.status) {
                setPosts(prev => prev.filter(p => p.id !== postId));
                if (editingPost?.id === postId) {
                    setEditingPost(null);
                    setActiveTab('articles');
                }
            } else {
                alert('Chyba pri odstraňovaní článku');
            }
        } catch (error) {
            alert('Chyba pri odstraňovaní článku');
        }
    };

    const handleFormSuccess = () => {
        fetchPosts();
        setActiveTab('articles');
        setEditingPost(null);
    };

    const handleCancel = () => {
        setActiveTab('articles');
        setEditingPost(null);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('sk-SK', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    useEffect(() => {
        fetchPosts();
    }, []);

    return (
        <BlogContainer>
            <BlogHeader>
                <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#000' }}>
                    Správa článkov
                </h1>
            </BlogHeader>

            <ButtonGroup>
                <TabButton
                    active={activeTab === 'articles'}
                    onClick={() => setActiveTab('articles')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    Zoznam článkov
                </TabButton>
                <TabButton
                    active={activeTab === 'create'}
                    onClick={() => {
                        setEditingPost(null);
                        setActiveTab('create');
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    {editingPost ? 'Upraviť článok' : 'Vytvoriť nový článok'}
                </TabButton>
            </ButtonGroup>

            {activeTab === 'articles' && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    {loading ? (
                        <LoadingState>Načítavam články...</LoadingState>
                    ) : posts.length === 0 ? (
                        <EmptyState
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            <h3>Zatiaľ žiadne články</h3>
                            <p>Začnite vytvorením prvého článku</p>
                            <TabButton
                                onClick={() => setActiveTab('create')}
                                style={{ marginTop: '1rem', display: 'inline-block' }}
                            >
                                Vytvoriť prvý článok
                            </TabButton>
                        </EmptyState>
                    ) : (
                        <PostsGrid>
                            {posts.map(post => (
                                <PostCard
                                    key={post.id}
                                    whileHover={{ scale: 1.02 }}
                                    onClick={() => handleEditPost(post.id)}
                                >
                                    <PostImage src={post.featuredImage}>
                                        {!post.featuredImage && 'Article'}
                                    </PostImage>
                                    <PostContent>
                                        <PostMeta>
                                            <CategoryBadge>{post.category}</CategoryBadge>
                                            <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                        {formatDate(post.createdAt)} • {post.readTime}
                      </span>
                                        </PostMeta>
                                        <PostTitle>{post.title}</PostTitle>
                                        {post.excerpt && <PostExcerpt>{post.excerpt}</PostExcerpt>}
                                        <PostActions>
                                            <ActionButton
                                                variant="edit"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditPost(post.id);
                                                }}
                                            >
                                                Upraviť
                                            </ActionButton>
                                            <ActionButton
                                                variant="delete"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeletePost(post.id);
                                                }}
                                            >
                                                Odstrániť
                                            </ActionButton>
                                        </PostActions>
                                    </PostContent>
                                </PostCard>
                            ))}
                        </PostsGrid>
                    )}
                </motion.div>
            )}

            {activeTab === 'create' && (
                <motion.div
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                >
                    <AdminPostForm
                        post={editingPost || undefined}
                        onSuccess={handleFormSuccess}
                        onCancel={handleCancel}
                    />
                </motion.div>
            )}
        </BlogContainer>
    );
};

export default AdminBlog;